/**
 * This class implements an Iterable that return timestamps
 * between two given timestamps at given interval.
 * The start time is clamped to the first timestamp before the given
 * start time, aligned With given interval. The end time is clamped
 * to the first timestamp after the given end time, aligned with the
 * given interval.
 * The end time is included in the iterator by default, unless the
 * inclusive parameter is set to false.
 * @param start start of the Iterator which will be clamped to first aligned timestamp before given start.
 * @param end end of the Iterator which will be clamped to first aligned timpstamp after given end
 * @param interval The interval to be used
 * @param inclusive flag indicating to include the end timestamp (defaults to true).
 * @returns Iterator over given time range with step size equal to given interval.
 */
export class Metronome {
    _start;
    _end;
    _interval;
    _count;
    constructor(_start, _end, _interval, _inclusive = true) {
        if (_interval > 0 && _start < _end) {
            const startMod = _start % _interval;
            const endMod = _end % _interval;
            _start = startMod > 0 ? _start - startMod : _start;
            _end = endMod > 0 ? _end - endMod + _interval : _end;
            this._count =
                Math.round((_end - _start) / _interval) + (_inclusive ? 1 : 0);
            this._start = _start;
            this._end = _end;
            this._interval = _interval;
        }
        else {
            throw new Error("Cannot create Metronome. Interval is not greater then 0 and/or start is not before end.");
        }
    }
    /**
     * @returns the actual start time of the iterator
     */
    start() {
        return this._start;
    }
    /**
     * @returns  the actual end time of the iterator
     */
    end() {
        return this._end;
    }
    /**
     * @returns the step size of the iterator
     */
    interval() {
        return this._interval;
    }
    /**
     * @returns The total number of timestamps the iterator will return
     */
    count() {
        return this._count;
    }
    /**
     * Calculate the timestamp for given index.
     * @param index The index where to calculate the timestamp for
     * @returns The timastamp at given index
     */
    get(index) {
        if (index >= 0 && index < this._count) {
            return this._start + index * this._interval;
        }
        else {
            throw new RangeError(`Metronome time index ${index} is not valid. 0 <= index < ${this._count}`);
        }
    }
    /**
     * Returns the iterator
     */
    *[Symbol.iterator]() {
        let _index = 0;
        while (_index < this._count) {
            yield this._start + _index++ * this._interval;
        }
    }
}
/**
 * Return a metronome that is an Iterable that returns timestamps
 * for a given time range at a specified interval.
 * The start time is clamped to the first timestamp before the start
 * of the given time range, aligned to given interval. The end time
 * is clamped to the first timestamp after the given end time, aligned
 * to the given interval.
 * The end time is included in the iterator by default, unless the
 * inclusive parameter is set to false.
 * @param start start of the Iterator which will be clamped to first aligned timestamp before given start.
 * @param end end of the Iterator which will be clamped to first aligned timpstamp after given end
 * @param interval The interval to be used
 * @param inclusive flag indicating to include the end timestamp (defaults to true).
 * @returns Iterator over given time range with step size equal to given interval.
 */
export const metronome = (start, end, interval, inclusive = true) => {
    return new Metronome(start, end, interval, inclusive);
};
